import type { BrokerCapabilities } from "../services/brokerIntegrationApi";
import { TRADINGVIEW_BROKER_SLUG } from "./tradingViewBroker";
import { TRADINGVIEW_PAPER_BROKER_SLUG } from "./tradingViewPaperBroker";

function normalizeSlug(brokerName: string): string {
  return brokerName.trim().toLowerCase();
}

/** Grid identity for the single TradingView brand card (not a lane slug until step 2). */
export const TRADINGVIEW_CONNECT_BRAND = TRADINGVIEW_BROKER_SLUG;

export type TradingViewLaneSlug =
  | typeof TRADINGVIEW_BROKER_SLUG
  | typeof TRADINGVIEW_PAPER_BROKER_SLUG;

export function isTradingViewConnectBrandSlug(
  brokerName: string | null | undefined
): boolean {
  const slug = normalizeSlug(String(brokerName || ""));
  return slug === TRADINGVIEW_BROKER_SLUG || slug === TRADINGVIEW_PAPER_BROKER_SLUG;
}

export function findTradingViewLaneCapability(
  brokers: BrokerCapabilities[],
  lane: TradingViewLaneSlug
): BrokerCapabilities | null {
  return (
    brokers.find((broker) => normalizeSlug(broker.broker_name) === lane) ?? null
  );
}

/**
 * Collapse `tradingview` + `tradingview_paper` into one grid card keyed as brand `tradingview`.
 * Prefer backtest capability for display_name; fall back to "TradingView".
 */
export function collapseBrokersForConnectWizard(
  brokers: BrokerCapabilities[]
): BrokerCapabilities[] {
  const backtest = findTradingViewLaneCapability(brokers, TRADINGVIEW_BROKER_SLUG);
  const paper = findTradingViewLaneCapability(brokers, TRADINGVIEW_PAPER_BROKER_SLUG);

  if (!backtest && !paper) return brokers;

  const brandSource = backtest ?? paper!;
  let brandDisplayName = (backtest?.display_name || "").trim();
  if (!brandDisplayName || /paper/i.test(brandDisplayName)) {
    brandDisplayName = "TradingView";
  } else {
    brandDisplayName =
      brandDisplayName.replace(/\s+Paper Trading$/i, "").trim() || "TradingView";
  }

  const brandCard: BrokerCapabilities = {
    ...brandSource,
    broker_name: TRADINGVIEW_CONNECT_BRAND,
    display_name: brandDisplayName,
  };

  return brokers
    .filter((broker) => !isTradingViewConnectBrandSlug(broker.broker_name))
    .concat(brandCard);
}

/** True when either TradingView lane already has a trade account. */
export function isTradingViewBrandConnected(
  connectedBrokerSlugs: Set<string>
): boolean {
  return (
    connectedBrokerSlugs.has(TRADINGVIEW_BROKER_SLUG) ||
    connectedBrokerSlugs.has(TRADINGVIEW_PAPER_BROKER_SLUG)
  );
}

export function defaultTradingViewLane(
  brokers: BrokerCapabilities[],
  preferred?: string | null
): TradingViewLaneSlug | null {
  const preferredSlug = normalizeSlug(String(preferred || ""));
  if (
    preferredSlug === TRADINGVIEW_PAPER_BROKER_SLUG &&
    findTradingViewLaneCapability(brokers, TRADINGVIEW_PAPER_BROKER_SLUG)
  ) {
    return TRADINGVIEW_PAPER_BROKER_SLUG;
  }
  if (
    preferredSlug === TRADINGVIEW_BROKER_SLUG &&
    findTradingViewLaneCapability(brokers, TRADINGVIEW_BROKER_SLUG)
  ) {
    return TRADINGVIEW_BROKER_SLUG;
  }
  if (findTradingViewLaneCapability(brokers, TRADINGVIEW_BROKER_SLUG)) {
    return TRADINGVIEW_BROKER_SLUG;
  }
  if (findTradingViewLaneCapability(brokers, TRADINGVIEW_PAPER_BROKER_SLUG)) {
    return TRADINGVIEW_PAPER_BROKER_SLUG;
  }
  return null;
}

export function tradingViewLaneTabLabel(lane: TradingViewLaneSlug): string {
  return lane === TRADINGVIEW_PAPER_BROKER_SLUG ? "Paper Trading" : "Backtesting";
}
